import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { LuCamera, LuUser, LuLock, LuSave } from 'react-icons/lu';

export default function Profile() {
  const { user, updateProfile } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [photo, setPhoto] = useState(user?.photo || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const fileRef = useRef(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSavingProfile(true);
    const result = await updateProfile(name.trim(), photo);
    if (result.success) toast.success('Profile updated');
    else toast.error(result.message);
    setSavingProfile(false);
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSavingPwd(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setSavingPwd(false); }
  };

  return (
    <div className="flex flex-col gap-7 max-w-2xl">
      <div>
        <h1 className="text-2xl font-extrabold text-hajj-dark">Edit Profile</h1>
        <p className="text-sm text-gray-400 mt-0.5">Update your display name and profile photo</p>
      </div>

      {/* Profile info */}
      <div className="card p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <LuUser className="text-hajj-teal text-lg" />
          <h2 className="text-sm font-bold text-hajj-dark">Profile Information</h2>
        </div>

        <form onSubmit={handleProfileSave} className="flex flex-col gap-5">
          {/* Photo */}
          <div className="flex items-center gap-5">
            <div className="relative flex-shrink-0">
              {photo ? (
                <img src={photo} alt="avatar" className="w-20 h-20 rounded-full object-cover border-4 border-hajj-gold/20" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-hajj-gold to-hajj-teal flex items-center justify-center text-white text-3xl font-bold border-4 border-hajj-gold/20">
                  {name?.[0]?.toUpperCase()}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-hajj-dark text-white flex items-center justify-center shadow-hajj-sm hover:bg-hajj-mid transition-colors"
              >
                <LuCamera size={13} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-hajj-dark">Profile Photo</p>
              <p className="text-xs text-gray-400 mt-0.5">JPG or PNG, max 2 MB</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="mt-2 text-xs font-semibold text-hajj-teal hover:underline"
              >
                Change photo
              </button>
              {photo && (
                <button
                  type="button"
                  onClick={() => setPhoto('')}
                  className="mt-2 ml-3 text-xs font-semibold text-red-400 hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              required
              className="input-field"
              placeholder="Your name"
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email Address</label>
            <input value={user?.email || ''} readOnly className="input-field bg-gray-50 text-gray-400 cursor-not-allowed" />
          </div>

          <div className="flex justify-end pt-1">
            <button type="submit" className="btn-primary" disabled={savingProfile}>
              {savingProfile && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              <LuSave size={14} />
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="card p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <LuLock className="text-hajj-teal text-lg" />
          <h2 className="text-sm font-bold text-hajj-dark">Change Password</h2>
        </div>

        <form onSubmit={handlePasswordSave} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              className="input-field"
              placeholder="Enter current password"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                className="input-field"
                placeholder="Min. 6 characters"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className="input-field"
                placeholder="Repeat new password"
              />
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button type="submit" className="btn-primary" disabled={savingPwd}>
              {savingPwd && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              <LuLock size={14} />
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
